import { useState } from 'react';
import {
  MAX_HAND_PICK_REMITTANCES,
  type CashReceiptDetail,
  type CashReceiptHandPickCandidate,
  type CashReceiptRemittanceLink,
  type GetCashReceiptHandPickResponse,
} from '@erp/rpc';
import { T, Var, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useArCashReceiptHandPick,
  useLinkCashReceiptRemittances,
  useUnlinkCashReceiptRemittance,
} from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import { useToast } from 'src/context/ToastContext';
import { useDebounce } from '@/hooks/useDebounce';
import { serializeMoney, sumMoney } from '@erp/shared-utils';
import { formatAmount, formatDate } from './cashReceiptFormat';
import { CashReceiptRemittanceLinksSection } from './cash-receipt-remittance-links-section';
import { CashReceiptSettlementBadge } from './CashReceiptSettlement';
import {
  projectSettlement,
  type ProjectedSettlement,
} from './handPickSettlement';

function OpenRemittanceLink({ remittanceId }: { remittanceId: string }) {
  return (
    <Link
      to="/accounts-receivable/remittances/$remittanceId"
      params={{ remittanceId }}
      viewTransition
      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
    >
      <ArrowSquareOut className="h-3.5 w-3.5" />
      <T context="link, open the remittance advice detail page from the cash receipt">
        Open
      </T>
    </Link>
  );
}

function CandidateRow({
  candidate,
  checked,
  disabled = false,
  onToggle,
}: {
  candidate: CashReceiptHandPickCandidate;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  const gt = useGT();
  const inputId = `hand-pick-${candidate.id}`;
  return (
    <li className="flex items-center gap-2 rounded-md border p-2">
      <Checkbox
        id={inputId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onToggle}
      />
      <label htmlFor={inputId} className="min-w-0 flex-1 cursor-pointer">
        <div className="flex items-center gap-2 text-sm">
          <span className="shrink-0 font-semibold tabular-nums">
            {formatAmount(candidate.statedTotalAmount)}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="truncate">
            {candidate.statedCustomerName?.trim() ||
              candidate.senderEmail ||
              gt('(No customer)', {
                context:
                  'AR cash receipt remittance card fallback when the advice names no customer or sender',
              })}
          </span>
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            {candidate.channel}
          </Badge>
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {[
            formatDate(candidate.statedPaymentDate ?? candidate.receivedAt),
            candidate.statedReferenceNo,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </label>
      <OpenRemittanceLink remittanceId={candidate.id} />
    </li>
  );
}

/** "Linked $X + picked $Y = $Z of $R", with the state a save would produce. */
function RunningSum({
  projected,
  receiptAmount,
}: {
  projected: ProjectedSettlement;
  receiptAmount: string | null;
}) {
  const remaining = projected.remaining;
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 p-2 text-sm"
      data-testid="cash-receipt-hand-pick-sum"
    >
      <CashReceiptSettlementBadge settlement={projected} />
      <span>
        <T context="AR cash receipt hand-pick running sum: linked remittances plus picked ones against the receipt amount">
          <Var>{formatAmount(projected.linkedTotal)}</Var> of{' '}
          <Var>{formatAmount(receiptAmount)}</Var> (picked{' '}
          <Var>{formatAmount(projected.pickedTotal)}</Var>)
        </T>
      </span>
      {remaining != null && Number(remaining) > 0 && (
        <span className="text-xs text-muted-foreground">
          <T context="AR cash receipt hand-pick, amount of the receipt not yet explained by remittances">
            <Var>{formatAmount(remaining)}</Var> left
          </T>
        </span>
      )}
      {remaining != null && Number(remaining) < 0 && (
        <span className="text-xs font-medium text-red-700">
          <T context="AR cash receipt hand-pick, picked remittances exceed the receipt by this amount">
            Over by <Var>{formatAmount(remaining.replace('-', ''))}</Var>
          </T>
        </span>
      )}
    </div>
  );
}

function UnlinkDialog({
  link,
  pending,
  onCancel,
  onConfirm,
}: {
  link: CashReceiptRemittanceLink | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const gt = useGT();
  const [reason, setReason] = useState('');
  return (
    <AlertDialog
      open={link != null}
      onOpenChange={(open) => !open && onCancel()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="confirm closing one remittance link on a cash receipt">
              Unlink this remittance?
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <T context="explains that unlinking a remittance keeps the closed link as history">
              The link is closed, not deleted: it stays in the receipt's history
              with your reason.
            </T>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={gt('Why is this remittance not part of the receipt?', {
            context:
              'placeholder, reason for unlinking a remittance from a cash receipt',
          })}
          aria-label={gt('Reason', {
            context:
              'label, reason for unlinking a remittance from a cash receipt',
          })}
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            <T context="dialog cancel button">Cancel</T>
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={pending || reason.trim() === ''}
            onClick={(e) => {
              e.preventDefault();
              onConfirm(reason.trim());
            }}
          >
            <T context="confirm button, close one remittance link on a cash receipt">
              Unlink
            </T>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function OverLinkDialog({
  open,
  remaining,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  remaining: string | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="confirm linking remittances whose totals exceed the cash receipt">
              Link more than the receipt amount?
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <T context="explains the over-link: the picked remittances state more than the bank received">
              These remittances state{' '}
              <Var>{formatAmount(remaining?.replace('-', '') ?? null)}</Var>{' '}
              more than the receipt. The receipt will read Over until someone
              untangles it.
            </T>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            <T context="dialog cancel button">Cancel</T>
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            <T context="confirm button, link remittances even though they exceed the receipt">
              Link anyway
            </T>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Error toast shared by link and unlink. */
function useFailureToast() {
  const { showToast } = useToast();
  return (title: string, err: unknown) =>
    showToast({
      title,
      message: err instanceof Error ? err.message : '',
      color: 'error',
      icon: 'warning',
    });
}

/**
 * Picked id → the candidate as picked, so a pick hidden by the search still
 * counts and can still be unchecked. Each pick reads the latest copy of its
 * row the server sent, so a corrected amount moves the running sum too.
 */
function usePickedRemittances(data: GetCashReceiptHandPickResponse) {
  const [stored, setStored] = useState<
    ReadonlyMap<string, CashReceiptHandPickCandidate>
  >(new Map());
  // A matcher's proposal arrives pre-selected, for a person to confirm.
  // Applied once per proposal (state adjusted during render, not in an
  // effect), so a search refetch never resets the person's own picks.
  const proposalKey = data.proposedRemittanceAdviceIds.join(',');
  const [appliedProposal, setAppliedProposal] = useState('');

  /** Replace the picks with exactly these remittances (a proposed set). */
  function pickExactly(ids: readonly string[]) {
    const wanted = new Set(ids);
    setStored(
      new Map(
        data.candidates
          .filter((c) => wanted.has(c.id))
          .slice(0, MAX_HAND_PICK_REMITTANCES)
          .map((c) => [c.id, c] as const),
      ),
    );
  }

  if (proposalKey !== appliedProposal) {
    setAppliedProposal(proposalKey);
    pickExactly(data.proposedRemittanceAdviceIds);
  }

  function toggle(candidate: CashReceiptHandPickCandidate) {
    setStored((prev) => {
      const next = new Map(prev);
      if (next.has(candidate.id)) next.delete(candidate.id);
      else if (next.size < MAX_HAND_PICK_REMITTANCES) {
        next.set(candidate.id, candidate);
      }
      return next;
    });
  }

  const fresh = new Map(data.candidates.map((c) => [c.id, c] as const));
  const picked = new Map(
    [...stored].map(([id, row]) => [id, fresh.get(id) ?? row] as const),
  );
  /** Picks the current list doesn't show: searched away, or taken since. */
  const unlisted = [...picked.values()].filter((c) => !fresh.has(c.id));

  return {
    picked,
    unlisted,
    atLimit: picked.size >= MAX_HAND_PICK_REMITTANCES,
    toggle,
    pickExactly,
    /** Drop these picks (the ones a save sent), keeping any made since. */
    remove: (ids: readonly string[]) =>
      setStored((prev) => {
        const next = new Map(prev);
        for (const id of ids) next.delete(id);
        return next;
      }),
  };
}

/**
 * The receipt's linked remittances (GEN-3103's section), with Unlink on
 * each: closing one asks for a reason and keeps the row as history.
 */
export function CashReceiptRemittanceLinksWithUnlink({
  receiptId,
  links,
}: {
  receiptId: string;
  links: CashReceiptRemittanceLink[];
}) {
  const gt = useGT();
  const failed = useFailureToast();
  const unlinkMutation = useUnlinkCashReceiptRemittance();
  const [target, setTarget] = useState<CashReceiptRemittanceLink | null>(null);

  async function unlink(reason: string) {
    if (!target) return;
    try {
      await unlinkMutation.mutateAsync({
        id: receiptId,
        linkId: target.linkId,
        reason,
      });
      setTarget(null);
    } catch (err) {
      failed(
        gt('Unlink failed', {
          context:
            'toast title, closing a remittance link on a cash receipt failed',
        }),
        err,
      );
    }
  }

  return (
    <>
      <CashReceiptRemittanceLinksSection links={links} onUnlink={setTarget} />
      <UnlinkDialog
        key={target?.linkId ?? 'closed'}
        link={target}
        pending={unlinkMutation.isPending}
        onCancel={() => setTarget(null)}
        onConfirm={(reason) => void unlink(reason)}
      />
    </>
  );
}

/**
 * Two same-source sets both add up to the receipt (the group matcher's
 * "ambiguous" verdict): each is offered, and choosing one replaces the picks.
 * Nothing is pre-selected.
 */
function ProposalChoices({
  choices,
  candidates,
  onChoose,
}: {
  choices: string[][];
  candidates: CashReceiptHandPickCandidate[];
  onChoose: (ids: readonly string[]) => void;
}) {
  if (choices.length === 0) return null;
  const totalById = new Map(
    candidates.map((c) => [c.id, c.statedTotalAmount] as const),
  );
  return (
    <div
      className="space-y-2 rounded-md border border-blue-200 bg-blue-50 p-2"
      data-testid="cash-receipt-hand-pick-proposals"
    >
      <p className="text-xs text-blue-900">
        <T context="AR cash receipt hand-pick: the group matcher found several sets of remittances that each add up to the receipt">
          More than one set of remittances from the same sender adds up to this
          receipt. Choose one to pick it, then check it before saving.
        </T>
      </p>
      <div className="flex flex-wrap gap-2">
        {choices.map((ids, index) => (
          <Button
            key={ids.join(',')}
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onChoose(ids)}
          >
            <T context="button, pick one proposed set of remittances; shows its number, how many remittances and their total">
              Set <Var>{index + 1}</Var>: <Var>{ids.length}</Var> remittances,{' '}
              <Var>
                {formatAmount(
                  serializeMoney(
                    sumMoney(ids.map((id) => totalById.get(id) ?? '0')),
                  ),
                )}
              </Var>
            </T>
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Picks the list no longer shows, still checked so they can be unchecked: a
 * search hid them, or someone linked them elsewhere and a save would fail.
 */
function UnlistedPicks({
  unlisted,
  onToggle,
}: {
  unlisted: CashReceiptHandPickCandidate[];
  onToggle: (candidate: CashReceiptHandPickCandidate) => void;
}) {
  if (unlisted.length === 0) return null;
  return (
    <div className="space-y-1" data-testid="cash-receipt-hand-pick-unlisted">
      <p className="text-xs text-muted-foreground">
        <T context="AR cash receipt hand-pick, picked remittances not shown in the list below because of the search or because they are no longer available">
          Picked, not in the list below (hidden by the search, or no longer
          available):
        </T>
      </p>
      <ul className="space-y-1">
        {unlisted.map((candidate) => (
          <CandidateRow
            key={candidate.id}
            candidate={candidate}
            checked
            onToggle={() => onToggle(candidate)}
          />
        ))}
      </ul>
    </div>
  );
}

function CandidateList({
  candidates,
  picked,
  atLimit,
  onToggle,
}: {
  candidates: CashReceiptHandPickCandidate[];
  picked: ReadonlyMap<string, CashReceiptHandPickCandidate>;
  atLimit: boolean;
  onToggle: (candidate: CashReceiptHandPickCandidate) => void;
}) {
  if (candidates.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        <T context="AR cash receipt hand-pick, no unlinked remittances fit under the receipt inside the match window">
          No unlinked remittances up to this receipt's amount inside the
          matching window.
        </T>
      </p>
    );
  }
  return (
    <ul className="max-h-80 space-y-1 overflow-y-auto">
      {candidates.map((candidate) => (
        <CandidateRow
          key={candidate.id}
          candidate={candidate}
          checked={picked.has(candidate.id)}
          disabled={atLimit && !picked.has(candidate.id)}
          onToggle={() => onToggle(candidate)}
        />
      ))}
    </ul>
  );
}

/**
 * Tick remittances, watch the running sum, save once. An over-link asks for
 * confirmation first.
 */
function RemittancePicker({
  receiptId,
  data,
  search,
  onSearch,
}: {
  receiptId: string;
  data: GetCashReceiptHandPickResponse;
  search: string;
  onSearch: (value: string) => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const failed = useFailureToast();
  const linkMutation = useLinkCashReceiptRemittances();
  const { picked, unlisted, atLimit, toggle, pickExactly, remove } =
    usePickedRemittances(data);
  const [confirmOverOpen, setConfirmOverOpen] = useState(false);
  const projected = projectSettlement({
    receiptAmount: data.receiptAmount,
    current: data.settlement,
    pickedTotals: [...picked.values()].map((c) => c.statedTotalAmount),
  });

  async function save(confirmOver: boolean) {
    const sent = [...picked.keys()];
    try {
      const { receipt: updated, linkedCount } = await linkMutation.mutateAsync({
        id: receiptId,
        remittanceAdviceIds: sent,
        confirmOver,
      });
      remove(sent);
      setConfirmOverOpen(false);
      const settled = updated.settlement.state === 'settled';
      showToast({
        title: gt('Linked {count} remittances', {
          context:
            'toast title, several remittances linked to one cash receipt',
          count: linkedCount,
        }),
        message: '',
        color: settled ? 'success' : 'warning',
        icon: settled ? 'check' : 'warning',
      });
    } catch (err) {
      failed(
        gt('Link failed', {
          context: 'toast title, cash receipt link to payment failed',
        }),
        err,
      );
    }
  }

  return (
    <>
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          <T context="AR cash receipt hand-pick, explanation above the list of remittances to pick">
            Pick the remittances this receipt pays. Their stated totals add up
            below; save links each one.
          </T>
        </p>
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={gt('Search payer, reference, sender or amount', {
            context:
              'placeholder, filter remittances to hand-pick for a cash receipt',
          })}
          className="h-8 text-sm"
        />
        <ProposalChoices
          choices={data.proposalChoices}
          candidates={data.candidates}
          onChoose={pickExactly}
        />
        <UnlistedPicks unlisted={unlisted} onToggle={toggle} />
        <CandidateList
          candidates={data.candidates}
          picked={picked}
          atLimit={atLimit}
          onToggle={toggle}
        />
        {atLimit && (
          <p className="text-xs text-muted-foreground">
            <T context="AR cash receipt hand-pick, the most remittances one save can link has been reached">
              One save links at most <Var>{MAX_HAND_PICK_REMITTANCES}</Var>{' '}
              remittances. Save these, then pick the rest.
            </T>
          </p>
        )}
      </div>

      <RunningSum projected={projected} receiptAmount={data.receiptAmount} />

      <Button
        size="sm"
        disabled={picked.size === 0 || linkMutation.isPending}
        onClick={() =>
          projected.state === 'over'
            ? setConfirmOverOpen(true)
            : void save(false)
        }
      >
        <T context="button, save the picked remittances as links to the cash receipt">
          Link <Var>{picked.size}</Var> picked
        </T>
      </Button>

      <OverLinkDialog
        open={confirmOverOpen}
        remaining={projected.remaining}
        pending={linkMutation.isPending}
        onCancel={() => setConfirmOverOpen(false)}
        onConfirm={() => void save(true)}
      />
    </>
  );
}

/**
 * Hand-pick several remittances for one receipt (a combined wire): tick
 * them, watch their stated totals add up against the receipt and the state
 * a save would produce, and save once. Each pick becomes its own link row.
 */
export function CashReceiptHandPickSection({
  receipt,
}: {
  receipt: CashReceiptDetail;
}) {
  const [search, setSearch] = useState('');
  // Folded unless the group matcher proposes a set: browsing every unlinked
  // remittance is a deliberate step, not the default view of a receipt. Null
  // until the person opens or closes it themselves.
  const [pickerOpen, setPickerOpen] = useState<boolean | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading, isError, refetch } = useArCashReceiptHandPick(
    receipt.id,
    debouncedSearch,
  );

  if (isError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">
          <T context="AR cash receipt hand-pick, failed to load linked and candidate remittances">
            Failed to load remittances to pick.
          </T>
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <T context="AR cash receipt work section, retry loading button">
            Retry
          </T>
        </Button>
      </div>
    );
  }
  if (isLoading || !data) return null;

  const hasProposal =
    data.proposedRemittanceAdviceIds.length > 0 ||
    data.proposalChoices.length > 0;
  const showPicker = pickerOpen ?? hasProposal;

  return (
    <div className="space-y-3" data-testid="cash-receipt-hand-pick">
      {showPicker ? (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              <T context="AR cash receipt, heading of the open picker for linking several remittances to one receipt">
                Link remittances
              </T>
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setPickerOpen(false)}
            >
              <T context="button, close the remittance picker on a cash receipt">
                Close
              </T>
            </Button>
          </div>
          <RemittancePicker
            receiptId={receipt.id}
            data={data}
            search={search}
            onSearch={setSearch}
          />
        </>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setPickerOpen(true)}
        >
          <T context="button, open the list of remittances to link to a cash receipt">
            Link Remittances
          </T>
        </Button>
      )}
    </div>
  );
}
